import debug from 'debug'
import { citiesWithDistricts, getSeoTownFromLocation } from '../../domain/seo/seo'
import { getSeoFooterCitiesPositions } from '../../domain/seo/seo-modulo'
import { selectFirstAddress } from '../address/address.selectors'
import { selectLocale } from '../navigation/navigation.selectors'
import { selectIsSeoCityWithDistricts, selectSeoTownDetails, selectSeoTownDetailsId } from './seo.selectors'

import { localeCountryIsBelgium, localeCountryIsFrance } from '../../domain/i18n'
import CityService from '../../domain/seo/CityService'
import DepartmentService from '../../domain/seo/DepartmentService'
import { formatCitiesLinks } from '../../domain/seo/footer/common/topSearchCities'
import { COUNT_CITIES_FOOTER_OTHER, COUNT_CITIES_TO_DISCOVER } from '../../domain/seo/seo.constants'
import { isCityWithDistrict } from '../../domain/utils/location'
import { MINIMAL_NB_OF_POI_TO_OPEN_CITY } from '../../parameters'
import { isFranceCountryCode, isFranceOrBelgiumCountryCode } from '../../routes/address/Countries'
import { uniqBy } from '../../utils/array'
import { setSeoCitiesForFooter, setSeoCitiesToDiscoverLinks, setSeoTownDetails } from './seoSlice'

const d = debug('seo')

export const searchSeoCityData = () => (dispatch, getState) => {
  const address = selectFirstAddress(getState())
  const seoTownFromLocation = getSeoTownFromLocation(address)
  if (!seoTownFromLocation || !isFranceOrBelgiumCountryCode(address.countryCode)) return Promise.resolve()

  const locale = selectLocale(getState())
  const town = CityService.findCityByPostCodeAndTownSlugified({ ...seoTownFromLocation, locale })
  const townWithMorePoiAndSameTownCode = CityService.findCityByTownCode({ towncode: address.towncode, locale })

  if (!town && !townWithMorePoiAndSameTownCode) return Promise.resolve()

  const townPlainObject = town
    ? {
        ...town,
        townWithMorePoiAndSameTownCode:
          (town?.poicount ?? 0) < MINIMAL_NB_OF_POI_TO_OPEN_CITY &&
          (townWithMorePoiAndSameTownCode?.poicount ?? 0) > MINIMAL_NB_OF_POI_TO_OPEN_CITY
            ? townWithMorePoiAndSameTownCode
            : null
      }
    : townWithMorePoiAndSameTownCode

  dispatch(
    setSeoTownDetails(
      // N.B.: for old towns, townWithoutDistrict is the new name
      {
        ...townPlainObject,
        townWithoutDistrict: address.town
      }
    )
  )
  return Promise.resolve()
}

export const setSeoCityDataFromGeocode = zip => (dispatch, getState) => {
  const address = selectFirstAddress(getState())

  dispatch(
    setSeoTownDetails({
      ...address,
      postcode: isCityWithDistrict(zip) ? zip : address.postcode,
      townWithoutDistrict: address.town
    })
  )
}

export const searchSeoCitiesForFooter = () => (dispatch, getState) => {
  const locale = selectLocale(getState())
  if (!localeCountryIsFrance(locale) && !localeCountryIsBelgium(locale)) return Promise.resolve()

  const hasDistrict = selectIsSeoCityWithDistricts(getState())
  if (d.enabled) d('searchSeoCitiesForFooter, hasDistrict:', hasDistrict)

  const searchMethod = hasDistrict ? searchSeoDistrictsForCity : searchSeoCitiesByPositions

  const cities = searchMethod()(dispatch, getState).map(({ town, postcode, district }) => ({
    town,
    postcode,
    district
  }))

  dispatch(setSeoCitiesForFooter(cities))
}

export const searchSeoCitiesByPositions = () => (dispatch, getState) => {
  const locale = selectLocale(getState())
  const count = CityService.findCityCountByLocale({ locale })

  const cityId = selectSeoTownDetailsId(getState())
  if (d.enabled) d('searchSeoCitiesByPositions, cityId', cityId)
  if (!cityId) return []

  const index = CityService.getIndexByPositionByPoi({ locale, cityId })
  if (d.enabled) d('searchSeoCitiesByPositions, index', index)

  const positions = getSeoFooterCitiesPositions(index, count, locale)
  if (d.enabled) d('searchSeoCitiesByPositions, positions', positions)

  const cities = CityService.findCitiesByPositionByPoi({ locale, positions })
  if (d.enabled)
    d(
      'searchSeoCitiesByPositions, cities',
      cities.map(({ towncode, town }) => `${town} (${towncode})`)
    )

  if (localeCountryIsFrance(locale)) {
    return uniqBy(
      [...citiesWithDistricts.map(city => ({ ...city, district: false })), ...cities],
      item => item.postcode
    )
  } else {
    return [...cities]
  }
}

export const searchSeoDistrictsForCity = () => (dispatch, getState) => {
  const address = selectFirstAddress(getState())
  if (!isFranceCountryCode(address.countryCode)) return Promise.resolve()

  const locale = selectLocale(getState())
  const postcode = selectSeoTownDetails(getState())?.postcode
  return CityService.findDistrictsByCityPostcode({ locale, postcode })
}

export const cleanCities = (cities = []) => cities.map(({ town, postcode, district }) => ({ town, postcode, district }))

export const searchFirstCities =
  ({ offset = 0, limit = COUNT_CITIES_FOOTER_OTHER }) =>
  (dispatch, getState) => {
    const locale = selectLocale(getState())
    const cities = CityService.findCitiesOrderedByPoi({ locale, offset, limit })
    dispatch(setSeoCitiesForFooter(cleanCities(cities)))
  }

export const loadCitiesToDiscover = () => (dispatch, getState) => {
  const locale = selectLocale(getState())
  const cities = CityService.findCitiesOrderedByPoi({ locale, offset: 0, limit: COUNT_CITIES_TO_DISCOVER })
  const payload = formatCitiesLinks(cleanCities(cities), locale)
  dispatch(setSeoCitiesToDiscoverLinks(payload))
}

export const searchDepartmentCities = () => (dispatch, getState) => {
  const { departmentCode, countryCode } = selectFirstAddress(getState())
  const cities = CityService.findTopCitiesFromDepartmentWithCode({
    departmentCode,
    countryCode
  })
  dispatch(setSeoCitiesForFooter(cleanCities(cities)))
}

export const searchRegionCities = () => (dispatch, getState) => {
  const { regionCode, countryCode } = selectFirstAddress(getState())
  const departmentsFromRegionsList = DepartmentService.findDepartmentsFromRegionCode(regionCode, countryCode)
  const departmentCodes = departmentsFromRegionsList?.map(department => department.code)
  const cities = CityService.findTopCitiesFromDepartmentsWithCode({
    departmentCodes,
    countryCode
  })
  dispatch(setSeoCitiesForFooter(cleanCities(cities)))
}

export const searchCountryCities = () => (dispatch, getState) => {
  const { countryCode } = selectFirstAddress(getState())
  if (!isFranceOrBelgiumCountryCode(countryCode)) return
  const locale = selectLocale(getState())
  const cities = CityService.findCitiesOrderedByPoi({ locale })
  dispatch(setSeoCitiesForFooter(cleanCities(cities)))
}
